import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { Plug, Copy, Check, Trash2, Loader2, KeyRound, ArrowRight, ChevronDown } from "lucide-react";
import { api, apiErr } from "../lib/api";
import { Card, GoldButton, GhostButton } from "./UI";

function fmtCountdown(sec) {
  const m = Math.floor(sec / 60), s = sec % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

export function LinkedPartners() {
  const [partners, setPartners] = useState([]);
  const [links, setLinks] = useState([]);
  const [selected, setSelected] = useState("");
  const [showCodeFallback, setShowCodeFallback] = useState(false);
  const [code, setCode] = useState(null);      // { code, expires_in_sec, partner_name }
  const [left, setLeft] = useState(0);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const timer = useRef(null);

  const load = useCallback(async () => {
    try {
      // /partners/active returns name + partner_id only; fetch full detail for connect_mode.
      const [a, l] = await Promise.all([api.get("/partners/active"), api.get("/me/partner-links")]);
      const base = a.data.partners || [];
      // Enrich each with connect_mode via a single pass over partner summary data
      // (added to /partners/active for Phase 3). Fallback: CODE.
      setPartners(base);
      setLinks(l.data.links || []);
      if (!selected && base.length) setSelected(base[0].partner_id);
    } catch (e) { /* non-blocking */ }
  }, [selected]);
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!code) return;
    clearInterval(timer.current);
    timer.current = setInterval(() => {
      setLeft((v) => { if (v <= 1) { clearInterval(timer.current); setCode(null); return 0; } return v - 1; });
    }, 1000);
    return () => clearInterval(timer.current);
  }, [code]);

  const linkedPartnerIds = new Set(links.map((l) => l.partner_id));
  const selectedPartner = partners.find((p) => p.partner_id === selected);
  const connectMode = (selectedPartner?.connect_mode || "CODE").toUpperCase();
  const canRedirect = connectMode === "REDIRECT" || connectMode === "BOTH";
  const canCode = connectMode === "CODE" || connectMode === "BOTH";
  const generate = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const { data } = await api.post("/me/partner-links/code", { partner_id: selected });
      setCode(data); setLeft(data.expires_in_sec || 900); setCopied(false);
      toast.success("Connect code generated");
    } catch (e) { toast.error(apiErr(e, "Could not generate code")); }
    finally { setBusy(false); }
  };
  const connect = async () => {
    if (!selected) return;
    setConnecting(true);
    try {
      const { data } = await api.post("/me/partner-connect/start", {
        partner_id: selected, origin: window.location.origin,
      });
      if (data?.authorization_url) {
        window.location.href = data.authorization_url;
      } else {
        toast.error("Partner did not return an authorization URL");
      }
    } catch (e) { toast.error(apiErr(e, "Could not start connection")); setConnecting(false); }
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(code.code); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* ignore */ }
  };
  const revoke = async (l) => {
    if (!window.confirm(`Unlink ${l.partner_name}? Future results from that site will stop counting until you link again.`)) return;
    try { await api.delete(`/me/partner-links/${l.id}`); toast.success("Partner unlinked"); setLinks((xs) => xs.filter((x) => x.id !== l.id)); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <Card className="p-6" data-testid="settings-linked-partners">
      <div className="flex items-center gap-2 mb-1">
        <Plug className="w-4 h-4 text-[#E5B842]" />
        <h2 className="font-heading text-lg font-bold uppercase tracking-tight text-slate-50">Connected Betting Accounts</h2>
      </div>
      <p className="text-[11px] font-mono uppercase tracking-wider text-slate-500 mb-4">
        Connect your betting account to include eligible activity in your WSTF competitive profile.
      </p>

      {/* Partner selector + primary action */}
      <div className="grid sm:grid-cols-[1fr_auto] gap-3 sm:items-end">
        <label className="block">
          <span className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-mono">Partner</span>
          <select value={selected} onChange={(e) => { setSelected(e.target.value); setCode(null); setShowCodeFallback(false); }} data-testid="linkpartner-select"
            className="mt-1.5 w-full bg-[#000000] border border-[#2b2512] rounded-lg px-3 py-2.5 text-slate-100 focus:outline-none focus:border-[#E5B842]/50">
            {partners.length === 0 && <option value="">No active partners</option>}
            {partners.map((p) => (
              <option key={p.partner_id} value={p.partner_id} disabled={linkedPartnerIds.has(p.partner_id)}>
                {p.name}{linkedPartnerIds.has(p.partner_id) ? " (connected)" : ""}
              </option>
            ))}
          </select>
        </label>
        {canRedirect ? (
          <GoldButton onClick={connect} disabled={connecting || !selected || linkedPartnerIds.has(selected)} data-testid="linkpartner-connect">
            {connecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />} Connect
          </GoldButton>
        ) : (
          <GoldButton onClick={generate} disabled={busy || !selected || linkedPartnerIds.has(selected)} data-testid="linkpartner-generate">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />} Generate link code
          </GoldButton>
        )}
      </div>
      {/* Code fallback (only when both modes available) */}
      {canRedirect && canCode && (
        <div className="mt-2.5">
          <button onClick={() => setShowCodeFallback((v) => !v)} data-testid="linkpartner-code-toggle"
            className="inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-wider text-slate-500 hover:text-[#E5B842]" style={{ transition: "color 0.15s ease" }}>
            Having trouble? Use connection code instead <ChevronDown className={`w-3 h-3 ${showCodeFallback ? "rotate-180" : ""}`} />
          </button>
          {showCodeFallback && (
            <div className="mt-2 flex items-center justify-end" data-testid="linkpartner-code-fallback">
              <GhostButton onClick={generate} disabled={busy || !selected || linkedPartnerIds.has(selected)} data-testid="linkpartner-generate">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />} Generate link code
              </GhostButton>
            </div>
          )}
        </div>
      )}

      {code && (
        <div className="mt-4 rounded-xl bg-[#0a0a0a] border border-[rgba(212,175,55,0.3)] p-4" data-testid="linkpartner-code-box">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono mb-1.5">
            Enter this code on {code.partner_name} · expires in <span className="text-[#E5B842]" data-testid="linkpartner-countdown">{fmtCountdown(left)}</span>
          </div>
          <div className="flex items-center gap-2">
            <code data-testid="linkpartner-code" className="flex-1 min-w-0 truncate bg-[#000] border border-[rgba(212,175,55,0.25)] rounded px-3 py-2.5 text-lg text-[#E5B842] font-mono tracking-widest">{code.code}</code>
            <button onClick={copy} data-testid="linkpartner-copy" className="shrink-0 inline-flex items-center gap-1 px-3 py-2.5 rounded bg-[#161616] border border-[rgba(212,175,55,0.25)] text-slate-300 hover:text-[#E5B842] text-xs" style={{ transition: "color 0.15s ease" }}>
              {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
            </button>
          </div>
        </div>
      )}

      {/* Connected accounts */}
      <div className="mt-6">
        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-mono mb-2">Connected accounts</div>
        {links.length === 0 ? (
          <div className="text-sm text-slate-500" data-testid="linkpartner-empty">No betting accounts connected yet.</div>
        ) : (
          <div className="space-y-2" data-testid="linkpartner-list">
            {links.map((l) => (
              <div key={l.id} data-testid={`linkpartner-row-${l.id}`} className="rounded-lg bg-[#0a0a0a] border border-[rgba(212,175,55,0.14)] p-3">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-emerald-400" />
                      <span className="font-heading font-bold text-slate-100 truncate">{l.partner_name || "Partner"}</span>
                      <span className="text-[10px] font-mono uppercase tracking-wider text-emerald-300">Connected</span>
                    </div>
                    <div className="text-[11px] font-mono text-slate-500 break-all mt-0.5">
                      Connected {l.linked_at ? new Date(l.linked_at).toLocaleDateString() : ""} · Automatic activity sync: ACTIVE
                    </div>
                  </div>
                  <GhostButton onClick={() => revoke(l)} data-testid={`linkpartner-revoke-${l.id}`} className="shrink-0 !text-rose-300 !border-rose-500/30">
                    <Trash2 className="w-4 h-4" /> Disconnect
                  </GhostButton>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}
